import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';

export const DAILY_MENU_DOCK_COUNTER_BAND_HEIGHT = 30;

export interface DailyMenuFlowCounterProps {
  label: string;
  count: number;
  selectedCount: number;
  isActive?: boolean;
}

function createDailyMenuFlowCounterStyles(c: ThemeColors) {
  const PILL_RADIUS = 18;
  return StyleSheet.create({
    counterItem: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: PILL_RADIUS,
      backgroundColor: '#fff0',
      minWidth: 56,
      height: 30,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderLight,
    },
    counterLabel: {
      fontSize: 11,
      fontWeight: '600',
      color: c.textSecondary,
      marginRight: 6,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    counterLabelActive: {
      color: c.primary,
    },
    counterBoxRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
    },
    counterBox: {
      width: 6,
      height: 6,
      borderRadius: 3,
      borderWidth: 1,
      borderColor: c.primaryFade,
      backgroundColor: c.primaryUltra,
    },
    counterBoxActive: {
      backgroundColor: c.primary + '50',
    },
    counterBoxFilled: {
      backgroundColor: c.primary,
    },
  });
}

const DailyMenuFlowCounterComponent: React.FC<DailyMenuFlowCounterProps> = ({ label, count, selectedCount, isActive = false }) => {
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createDailyMenuFlowCounterStyles(colors), [colors]);

  const boxes = useMemo(() => {
    return Array.from({ length: count }, (_, idx) => {
      const isFilled = idx < selectedCount;

      return <View key={`${label}-${idx}`} style={[styles.counterBox, isActive && styles.counterBoxActive, isFilled && styles.counterBoxFilled]} />;
    });
  }, [count, selectedCount, isActive, label, styles]);

  return (
    <View style={styles.counterItem}>
      <Text style={[styles.counterLabel, isActive && styles.counterLabelActive]} numberOfLines={1}>
        {label}
      </Text>
      <View style={styles.counterBoxRow}>{boxes}</View>
    </View>
  );
};

export const DailyMenuFlowCounter = memo(DailyMenuFlowCounterComponent);
